import ArrowForwardOutlined from '@icons/ArrowForwardOutlined';
import { Box, Fab, Typography } from '@mui/material';
import { UserNotification, ViewPermission } from '@types';
import dayjs from 'dayjs';
import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { extractNameFromEmail } from '../../utils';

function isViewPermission(obj: unknown): obj is ViewPermission {
  if (typeof obj === 'object' && obj !== null) {
    const o = obj as Record<string, unknown>;
    return typeof o.view_id === 'string' && typeof o.invited_by === 'string';
  }
  return false;
}
export interface NavbarMailboxViewAccessNotificationProps {
  notification: UserNotification;
  onNavigate: (id: string) => void;
}

const NavbarMailboxViewAccessNotification = ({ notification, onNavigate }: NavbarMailboxViewAccessNotificationProps) => {
  const navigate = useNavigate();
  const createdAtRelative = useMemo(() => {
    return dayjs(notification.created_at).fromNow();
  }, [notification.created_at]);

  const data = useMemo(() => {
    if (isViewPermission(notification.data)) {
      return notification.data;
    }
    return null;
  }, [notification.data]);

  const name = useMemo(() => {
    if (!data) return '';
    return extractNameFromEmail(data.invited_by);
  }, [data]);

  const onClicked = useCallback(() => {
    if (!data) return;
    navigate(`/view/${data.view_id}`);
    onNavigate(notification.id);
  }, [data, navigate, notification.id, onNavigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        p: 2,
        flexDirection: 'column',
        gap: 0.5
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            // \n
            flex: 1,
            color: 'text.secondary',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '166%',
            letterSpacing: '0px'
          }}
        >
          New View
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 0.5,
            alignItems: 'center'
          }}
        >
          <Typography
            sx={{
              // \n
              color: 'text.secondary',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '166%',
              letterSpacing: '0px'
            }}
          >
            {createdAtRelative}
          </Typography>
          {notification.seen ? (
            <Box sx={{ width: 8 }} />
          ) : (
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'primary.main'
              }}
            />
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '14px',
            letterSpacing: '0px',
            lineHeight: '143%',
            flex: 1
          }}
        >
          {notification.message ? `${name} sent you a new view with message: "${notification.message}".` : `${name} sent you a new view.`}
        </Typography>

        <Fab size="small" color="primary" sx={{ color: 'white' }} onClick={onClicked}>
          <ArrowForwardOutlined />
        </Fab>
      </Box>
    </Box>
  );
};
export default memo(NavbarMailboxViewAccessNotification);
